/**
 * CompanyMembershipContext V2 - Simplified
 * 
 * Simple, deterministic company membership loading.
 * No retries, no magic, just straightforward reads.
 * 
 * V1 Behavior:
 * - On signup: always creates new company + admin user
 * - On login: reads /users/{uid} → companyId → /companies/{companyId}/users/{uid} → role
 * 
 * TODO (v2+): Add invite code support for joining existing companies
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { createCompanyV2 } from '../utils/companyMembership';
import { getPermissionsForRole } from '../types/permissions';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';

// Export createCompanyV2 for use in signup (backward compatibility)
export { createCompanyV2 };

const CompanyMembershipContext = createContext();

export function useCompanyMembership() {
  return useContext(CompanyMembershipContext);
}

/**
 * CompanyMembershipProvider V2
 * 
 * Provides:
 * - membership: { companyId, role, isActive, companyName? } | null
 * - loading: boolean
 * - error: string | null
 * - permissions: Permissions object based on role
 */
export function CompanyMembershipProvider({ children }) {
  const { currentUser, loading: authLoading } = useAuth();
  const [membership, setMembership] = useState(null);
  const [company, setCompany] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) {
      setLoading(true);
      return;
    }

    // No user = no membership
    if (!currentUser) {
      setMembership(null);
      setCompany(null);
      setPermissions(null);
      setLoading(false);
      setError(null);
      return;
    }

    // User exists - set up reactive listener on user document
    console.log('[membership] Setting up onSnapshot listener for user:', currentUser.uid);
    setLoading(true);
    setError(null);

    const userRef = doc(db, 'users', currentUser.uid);
    
    // Set up onSnapshot listener to react to user doc changes
    const unsubscribe = onSnapshot(
      userRef,
      async (userDoc) => {
        console.log('[membership] onSnapshot user doc changed:', userDoc.exists() ? 'exists' : 'not found');
        
        if (!userDoc.exists()) {
          console.warn('[membership] ⚠️ User document does not exist yet:', currentUser.uid);
          // Keep loading = true, don't set membership to null permanently
          // The doc will be created during signup and listener will fire again
          return;
        }

        const userData = userDoc.data();
        console.log('[membership] onSnapshot user doc data:', { hasCompanyId: !!userData.companyId });

        // Check if user doc has companyId
        if (!userData.companyId) {
          console.warn('[membership] ⚠️ User document exists but has no companyId yet');
          // Keep loading = true, don't set membership to null permanently
          // companyId will be added during signup and listener will fire again
          return;
        }

        const companyId = userData.companyId;
        console.log('[membership] companyId found:', companyId);

        // User doc has companyId - load company user document (membership)
        // Add retry logic in case there's a timing issue during signup
        try {
          let companyUserDoc = null;
          let retries = 3;
          while (retries > 0 && !companyUserDoc?.exists()) {
            try {
              const companyUserRef = doc(db, 'companies', companyId, 'users', currentUser.uid);
              companyUserDoc = await getDoc(companyUserRef);

              if (!companyUserDoc.exists() && retries > 1) {
                console.log('[membership] Company user doc not found yet, retrying...', retries - 1, 'retries left');
                // Wait a bit before retrying (exponential backoff)
                await new Promise(resolve => setTimeout(resolve, 200 * (4 - retries)));
                retries--;
              } else {
                break;
              }
            } catch (err) {
              console.error('[membership] Error reading company user doc:', err);
              retries--;
              if (retries > 0) {
                await new Promise(resolve => setTimeout(resolve, 200));
              }
            }
          }

          if (!companyUserDoc?.exists()) {
          console.warn('[membership] ⚠️ Company user document not found after retries:', companyId, currentUser.uid);
          
          // If company user doc still doesn't exist, check if user is the company owner
          // If so, create it with admin role (this handles edge cases during signup)
          try {
            const companyRef = doc(db, 'companies', companyId);
            const companyDoc = await getDoc(companyRef);
            
            if (companyDoc.exists() && companyDoc.data().ownerUid === currentUser.uid) {
              console.log('[membership] User is company owner, creating company user doc with admin role...');
              const companyUserRef = doc(db, 'companies', companyId, 'users', currentUser.uid);
              await setDoc(companyUserRef, {
                email: currentUser.email || '',
                name: userData.name || currentUser.displayName || '',
                role: 'admin',
                isActive: true,
                createdAt: new Date().toISOString(),
              });
              
              // Re-read the doc
              companyUserDoc = await getDoc(companyUserRef);
              
              if (companyUserDoc.exists()) {
                console.log('[membership] ✅ Company user document created successfully');
              } else {
                console.error('[membership] ❌ Failed to create company user document');
                setError('Failed to set up user membership. Please contact support.');
                setMembership(null);
                setCompany(null);
                setPermissions(null);
                setLoading(false);
                return;
              }
            } else {
              // Not the owner and doc doesn't exist - something is wrong
              console.error('[membership] ❌ Company user document not found and user is not owner');
              setError('Company user document not found. Please contact support.');
              setMembership(null);
              setCompany(null);
              setPermissions(null);
              setLoading(false);
              return;
            }
          } catch (createError) {
            console.error('[membership] ❌ Error creating company user document:', createError);
            setError('Failed to set up user membership. Please contact support.');
            setMembership(null);
            setCompany(null);
            setPermissions(null);
            setLoading(false);
            return;
          }
        }

        // Now we have companyUserDoc - build membership object
        const companyUserData = companyUserDoc.data();
        console.log('[membership] loaded company user:', { role: companyUserData.role, isActive: companyUserData.isActive });

        // Build membership object
        const membershipData = {
          companyId: companyId,
          role: companyUserData.role || 'tech',
          isActive: companyUserData.isActive !== false,
          userData: companyUserData,
        };

        // Load company document (optional, for company name/details)
        let companyData = null;
        try {
          const companyRef = doc(db, 'companies', companyId);
          const companyDoc = await getDoc(companyRef);
          if (companyDoc.exists()) {
            companyData = {
              id: companyDoc.id,
              ...companyDoc.data(),
            };
            membershipData.companyName = companyDoc.data().name;
            console.log('[membership] loaded company document:', companyDoc.data().name);
          }
        } catch (err) {
          console.warn('[membership] Could not load company document:', err);
          // Non-critical, continue without company document
        }

        // Set membership and permissions
        console.log('[membership] ✅ Membership loaded successfully:', membershipData);
        setMembership(membershipData);
        setCompany(companyData);
        
        // Set permissions based on role
        const rolePermissions = getPermissionsForRole(membershipData.role);
        setPermissions(rolePermissions);
        setLoading(false);
        setError(null);

        // Update last login timestamp (non-blocking)
        try {
          const companyUserRef = doc(db, 'companies', companyId, 'users', currentUser.uid);
          await setDoc(companyUserRef, {
            lastLoginAt: new Date().toISOString(),
          }, { merge: true });
        } catch (err) {
          console.warn('[membership] Could not update last login:', err);
          // Non-critical
        }

        } catch (err) {
          console.error('[membership] ❌ Error loading company user document:', err);
          setError(err.message || 'Failed to load company membership');
          setMembership(null);
          setCompany(null);
          setPermissions(null);
          setLoading(false);
        }
      },
      (err) => {
        console.error('[membership] ❌ onSnapshot error:', err);
        setError(err.message || 'Failed to listen to user document');
        setMembership(null);
        setCompany(null);
        setPermissions(null);
        setLoading(false);
      }
    );

    // Clean up listener when user changes or component unmounts
    return () => {
      console.log('[membership] Cleaning up onSnapshot listener');
      unsubscribe();
    };
  }, [currentUser, authLoading]);

  // Update company profile (for admin users)
  const updateCompanyProfile = async (profileData) => {
    if (!membership?.companyId) {
      throw new Error('No company loaded');
    }

    try {
      const companyRef = doc(db, 'companies', membership.companyId);
      await setDoc(companyRef, {
        ...company,
        ...profileData,
        updatedAt: new Date().toISOString(),
      }, { merge: true });

      // Reload company document
      const companyDoc = await getDoc(companyRef);
      if (companyDoc.exists()) {
        setCompany({
          id: companyDoc.id,
          ...companyDoc.data(),
        });
      }
    } catch (error) {
      console.error('[CompanyMembershipContext] Error updating company profile:', error);
      throw error;
    }
  };

  // Computed values for backward compatibility
  const companyId = membership?.companyId || null;
  const currentUserRole = membership?.role || null;
  const userData = membership?.userData || null;

  const value = {
    // Membership data
    membership,
    companyId,
    currentUserRole,
    userData,
    company,
    
    // Permissions
    permissions,
    
    // State
    isLoading: loading,
    error,
    
    // Functions
    updateCompanyProfile,
    
    // Backward compatibility (mapped from membership)
    currentCompany: company,
    refreshMembership: async () => {
      // Note: No longer needed with onSnapshot approach
      // The listener will automatically update when documents change
      console.log('[membership] refreshMembership called - not needed with reactive listeners');
    },
  };

  return (
    <CompanyMembershipContext.Provider value={value}>
      {children}
    </CompanyMembershipContext.Provider>
  );
}

